import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SidebarItemProps } from "../types";
import { useTheme } from "src/context/ThemeContext";

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  isMinimized = false,
  onItemClick,
  isExpanded = false,
  activeItemId,
  activeParentId,
  depth = 0,
  expandedItems = [],
}) => {
  const {
    id,
    title,
    icon,
    path,
    badgeCount,
    badgeColor = "primary",
    isActive = false,
    onClick,
    children,
  } = item;

  const { theme } = useTheme();

  // Remove local expansion state - now controlled by parent

  // Helper function to determine padding based on depth and HTML design
  const getPaddingClasses = (itemDepth: number) => {
    // Top-level items (depth 0): exactly match HTML design px-3 py-2
    // Child items (depth 1+): exactly match HTML design px-4 py-2
    return itemDepth === 0 ? "tw-px-3 tw-py-2" : "tw-px-4 tw-py-2";
  };

  // Helper function to generate tooltip content for parent items with children
  const generateTooltipContent = (parentTitle: string, children: any[]) => {
    if (!children || children.length === 0) return parentTitle;
    
    const childTitles = children.map(child => child.title).join(', ');
    return `${parentTitle}: ${childTitles}`;
  };

  const handleClick = () => {
    if (onItemClick) {
      onItemClick(item);
    }
    if (onClick) {
      onClick();
    }
  };

  const getBadgeColorClasses = (color: string) => {
    const colorMap = {
      primary: theme === 'light' ? "tw-bg-primary tw-text-white" : "tw-bg-blue-600 tw-text-white",
      success: theme === 'light' ? "tw-bg-green-500 tw-text-white" : "tw-bg-emerald-500 tw-text-white",
      warning: theme === 'light' ? "tw-bg-yellow-500 tw-text-white" : "tw-bg-amber-500 tw-text-white",
      danger: "tw-bg-red-500 tw-text-white",
      info: "tw-bg-blue-500 tw-text-white", // Blue color for handled chats
    };
    return colorMap[color] || colorMap.primary;
  };

  const getBadgeBackgroundClass = (color: string) => {
    const bgColorMap = {
      primary: theme === 'light' ? "tw-bg-primary" : "tw-bg-blue-600",
      success: theme === 'light' ? "tw-bg-green-500" : "tw-bg-emerald-500",
      warning: theme === 'light' ? "tw-bg-yellow-500" : "tw-bg-amber-500",
      danger: "tw-bg-red-500", // Red for incoming chats
      info: "tw-bg-blue-500", // Blue for handled chats
    };
    return bgColorMap[color] || bgColorMap.primary;
  };

  // For items with children (like Settings), render exactly like HTML design
  if (children && children.length > 0) {
    // Parent button with conditional active styling and depth-based padding
    const paddingClasses = getPaddingClasses(depth);
    const settingsClasses = `
      tw-flex tw-w-full tw-items-center tw-rounded-lg tw-transition-colors tw-duration-200 ${paddingClasses}
      ${
        isActive
          ? theme === 'light'
            ? "tw-bg-[#EBF5FF] tw-text-[#007AFF]"
            : "tw-bg-blue-600 tw-text-white"
          : theme === 'light'
            ? "tw-text-slate-600 hover:tw-bg-slate-100"
            : "!tw-text-slate-50 hover:tw-bg-slate-600"
      }
      ${isMinimized ? "tw-justify-center" : "tw-justify-between"}
    `.trim();

    return (
      <div>
        {/* Settings button matching HTML lines 260-279 with proper minimized state */}
        <button 
          onClick={handleClick} 
          className={settingsClasses}
          {...(isMinimized && {
            'data-sidebar-tooltip': generateTooltipContent(title, children)
          })}
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            <span
              className={`material-symbols-outlined ${
                isActive ? "filled" : ""
              } ${
                isActive
                  ? theme === 'light' ? "tw-text-[#007AFF]" : "tw-text-white"
                  : theme === 'light' ? "tw-text-slate-500" : "!tw-text-slate-50"
              }`}
            >
              {icon}
            </span>
            {!isMinimized && (
              <span className="tw-text-sm tw-font-medium">{title}</span>
            )}
          </div>
          {!isMinimized && (
            <span
              className={`material-symbols-outlined tw-text-lg ${
                isActive
                  ? theme === 'light' ? "tw-text-[#007AFF]" : "tw-text-white"
                  : theme === 'light' ? "tw-text-slate-400" : "!tw-text-slate-50"
              } tw-transition-transform tw-duration-200 ${
                isExpanded ? "tw-rotate-180" : ""
              }`}
            >
              expand_more
            </span>
          )}
        </button>

        {/* Submenu with smooth expand/collapse animation */}
        {!isMinimized && (
          <div
            className={`tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out ${
              isExpanded
                ? "tw-max-h-[500px] tw-opacity-100 tw-transform tw-translate-y-0"
                : "tw-max-h-0 tw-opacity-0 tw-transform -tw-translate-y-2"
            }`}
          >
            <div className="tw-mt-1 tw-space-y-1 tw-pl-6">
              {children.map((child) => {
                // If child has its own children, render as nested SidebarItem (for Nested-Menu type)
                if (child.children && child.children.length > 0) {
                  return (
                    <SidebarItem
                      key={child.id}
                      item={{
                        ...child,
                        isActive: activeItemId === child.id,
                      }}
                      isMinimized={isMinimized}
                      onItemClick={onItemClick}
                      isExpanded={expandedItems.includes(child.id)}
                      activeItemId={activeItemId}
                      activeParentId={activeParentId}
                      depth={depth + 1}
                      expandedItems={expandedItems}
                    />
                  );
                }

                // Otherwise render as simple Link with active state - matching HTML design
                const isChildActive = activeItemId === child.id;
                const childPaddingClasses = getPaddingClasses(depth + 1);
                return (
                  <Link
                    key={child.id}
                    to={child.path}
                    className={`tw-block tw-rounded-md tw-transition-colors tw-duration-200 ${childPaddingClasses} tw-text-sm tw-font-medium ${
                      isChildActive
                        ? theme === 'light'
                          ? "tw-bg-[#EBF5FF] tw-text-[#007AFF]"
                          : "tw-bg-blue-600 tw-text-white"
                        : theme === 'light'
                          ? "tw-text-slate-600 hover:tw-bg-slate-100"
                          : "!tw-text-slate-50 hover:tw-bg-slate-600"
                    }`}
                    onClick={() => {
                      // Trigger the parent's onItemClick for child items to close sidebar
                      if (onItemClick) {
                        onItemClick(child);
                      }
                    }}
                  >
                    {child.title}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular navigation items (Dashboard, Product Listing, etc.) - HTML lines 173-227
  // Use unified class structure with depth-based padding
  const paddingClasses = getPaddingClasses(depth);

  // Special styling for logout button to match HTML design
  const isLogoutButton = id === "logout";

  const navigationClasses = `
    tw-relative tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-lg tw-transition-colors tw-duration-200 ${paddingClasses}
    ${
      isLogoutButton
        ? theme === 'light'
          ? "tw-text-red-500 hover:tw-bg-red-50"
          : "tw-text-red-400 hover:tw-bg-red-900/20"
        : isActive
        ? theme === 'light'
          ? "tw-bg-[#EBF5FF] tw-text-[#007AFF]"
          : "tw-bg-blue-600 tw-text-white"
        : theme === 'light'
          ? "tw-text-slate-600 hover:tw-bg-slate-100"
          : "!tw-text-slate-50 hover:tw-bg-slate-600"
    }
    ${isMinimized ? "tw-justify-center" : ""}
  `.trim();

  // Icon classes - handle logout button's red color and other states
  const iconClasses = isLogoutButton
    ? `material-symbols-outlined ${theme === 'light' ? 'tw-text-red-500' : 'tw-text-red-400'}`
    : isActive
    ? `material-symbols-outlined filled ${theme === 'light' ? 'tw-text-[#007AFF]' : 'tw-text-white'}`
    : `material-symbols-outlined ${theme === 'light' ? 'tw-text-slate-500' : '!tw-text-slate-50'}`;

  const content = (
    <>
      <span className={iconClasses}>{icon}</span>

      {!isMinimized && (
        <>
          <span className="tw-text-sm tw-font-medium">{title}</span>

          {badgeCount !== undefined && badgeCount >= 0 && (
            <span
              className={`tw-ml-auto tw-flex tw-h-5 tw-min-w-[20px] tw-items-center tw-justify-center tw-rounded-full tw-px-1.5 tw-text-xs tw-font-medium badge-${
                badgeColor || "primary"
              } ${getBadgeColorClasses(badgeColor || "primary")}`}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
        </>
      )}

      {/* Badge for minimized state - matching HTML lines 220-226 */}
      {isMinimized && badgeCount !== undefined && badgeCount >= 0 && (
        <span className="tw-absolute tw-top-1 tw-right-1 tw-flex tw-h-2 tw-w-2">
          <span
            className={`tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full badge-${
              badgeColor || "primary"
            } ${getBadgeBackgroundClass(badgeColor || "primary")}`}
          />
        </span>
      )}

      {/* No tooltip in minimized state - matching Settings implementation */}
    </>
  );

  // Render all navigation items as buttons (same as Settings)
  const navigate = useNavigate();

  const handleNavigation = () => {
    handleClick();
    if (path && path !== "#") {
      navigate(path);
    }
  };

  return (
    <button
      type="button"
      className={navigationClasses}
      onClick={handleNavigation}
      {...(isMinimized && {
        'data-sidebar-tooltip': title
      })}
    >
      {content}
    </button>
  );
};

export default SidebarItem;
